import { useCallback, useEffect, useMemo, useState } from "react";
import type { CustomFieldsSchema } from "@forgepoint/shared";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useCompanySettingsStore } from "@/store/companySettingsStore";
import { UnsavedChangesBar } from "@/modules/standardAgent/components/UnsavedChangesBar";
import { CustomFieldsSchemaEditor } from "./CustomFieldsSchemaEditor";
import { SectionLabel } from "./SectionLabel";

interface CompanySettingsPanelProps {
  isAdmin: boolean;
}

/**
 * Settings panel for the Company Settings tab: the company custom fields
 * schema, with a sticky unsaved-changes bar.
 */
export function CompanySettingsPanel({ isAdmin }: CompanySettingsPanelProps) {
  const settings = useCompanySettingsStore((state) => state.settings);
  const isLoading = useCompanySettingsStore((state) => state.isLoading);
  const isSaving = useCompanySettingsStore((state) => state.isSaving);
  const hasFetched = useCompanySettingsStore((state) => state.hasFetched);
  const fetchSettings = useCompanySettingsStore((state) => state.fetchSettings);
  const updateSettings = useCompanySettingsStore(
    (state) => state.updateSettings,
  );
  const [savedSchema, setSavedSchema] = useState<CustomFieldsSchema | null>(
    null,
  );
  const [schema, setSchema] = useState<CustomFieldsSchema | null>(null);

  const isDirty = useMemo(
    () => JSON.stringify(schema) !== JSON.stringify(savedSchema),
    [savedSchema, schema],
  );

  useEffect(() => {
    if (hasFetched) {
      return;
    }
    fetchSettings().then(() => {
      const error = useCompanySettingsStore.getState().error;
      if (error) {
        toast.error(error);
      }
    });
  }, [fetchSettings, hasFetched]);

  useEffect(() => {
    if (!hasFetched) {
      return;
    }
    const loaded = settings?.customFieldsSchema ?? null;
    setSavedSchema(loaded);
    setSchema(loaded);
  }, [hasFetched, settings]);

  const save = useCallback(async () => {
    const updatedSettings = await updateSettings(schema);
    if (!updatedSettings) {
      toast.error(
        useCompanySettingsStore.getState().error ??
          "Failed to save company custom fields.",
      );
      return;
    }
    const saved = updatedSettings.customFieldsSchema;
    setSavedSchema(saved);
    setSchema(saved);
    toast.success("Company settings saved.");
  }, [schema, updateSettings]);

  const discard = useCallback(() => {
    setSchema(savedSchema);
  }, [savedSchema]);

  return (
    <div className="space-y-6 pt-4">
      <SectionLabel title="Custom Fields" id="company-settings-custom-fields" />
      {!hasFetched || isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <Card className="shadow-sm overflow-hidden">
          <div className="px-4 py-3.5">
            <p className="text-sm font-medium mb-1">Company custom fields</p>
            <p className="text-xs text-muted-foreground mb-3">
              Define organization-wide fields stored on companies.
            </p>
            <CustomFieldsSchemaEditor
              schema={schema}
              onChange={setSchema}
              disabled={!isAdmin || isSaving}
            />
          </div>
        </Card>
      )}

      <UnsavedChangesBar
        isDirty={isAdmin && isDirty}
        onSave={save}
        onDiscard={discard}
        isSaving={isSaving}
      />
    </div>
  );
}
